import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { DateChangeEvent } from 'carbon-react/lib/components/date';

import {
  ErrorMessageTranslation,
  WizardFormErrors
} from '@futrli/ui-components/components/sidebar/wizard/types';

import { ForecastFormElementIds } from '../types';
import {
  ForecastStartDateRange,
  isForecastStartDateOutOfRange,
  useForecastStartDateRange
} from './useForecastStartDateRange';

const DATE_FORMAT_REGEX = /^\d\d\/\d\d\/\d\d\d\d$/;

interface UseForecastStartDateFieldParams {
  initialFrom?: string;
  organisationCreated?: string;
  setErrors: Dispatch<SetStateAction<WizardFormErrors>>;
}

const getOutOfRangeError = (intl: IntlShape): ErrorMessageTranslation => ({
  id: ForecastFormElementIds.From,
  message: intl.formatMessage({
    id: 'predict.create_forecast.validation.start_date_is_out_of_range',
    defaultMessage: 'Start date is out of range'
  })
});

const getRangeValidationError = (
  startDate: string,
  intl: IntlShape,
  startDateRange: ForecastStartDateRange
) => {
  if (
    !DATE_FORMAT_REGEX.test(startDate.trim()) ||
    !isForecastStartDateOutOfRange(startDate, startDateRange)
  ) {
    return undefined;
  }

  return getOutOfRangeError(intl);
};

const getValidationError = (
  startDate: string,
  intl: IntlShape,
  startDateRange: ForecastStartDateRange
): ErrorMessageTranslation | undefined => {
  if (!startDate.trim().length) {
    return {
      id: ForecastFormElementIds.From,
      message: intl.formatMessage({
        id: 'predict.create_forecast.validation.start_date_is_required',
        defaultMessage: 'Start date is required'
      })
    };
  }

  if (!DATE_FORMAT_REGEX.test(startDate.trim())) {
    return {
      id: ForecastFormElementIds.From,
      message: intl.formatMessage({
        id: 'predict.create_forecast.validation.enter_valid_date_format',
        defaultMessage: 'Enter valid date format (DD/MM/YYYY)'
      })
    };
  }

  return getRangeValidationError(startDate, intl, startDateRange);
};

/**
 * Manages the shared Start-date state and validation used by new forecast forms.
 * The latest value is retained synchronously so submit validation cannot read stale React state.
 */
export const useForecastStartDateField = ({
  initialFrom = '',
  organisationCreated,
  setErrors
}: UseForecastStartDateFieldParams) => {
  const [from, setFromState] = useState(initialFrom);
  const latestFrom = useRef(initialFrom);
  const intl = useIntl();
  const startDateRange = useForecastStartDateRange(organisationCreated);

  const setFrom = useCallback((startDate: string) => {
    latestFrom.current = startDate;
    setFromState(startDate);
  }, []);

  const getStartDate = useCallback(() => latestFrom.current, []);

  const validateStartDate = useCallback(
    () => getValidationError(latestFrom.current, intl, startDateRange),
    [intl, startDateRange]
  );

  const onChangeFrom = useCallback(
    (event: DateChangeEvent) => {
      const startDate = event.target.value.formattedValue;
      const rangeError = getRangeValidationError(startDate, intl, startDateRange);
      const outOfRangeMessage = getOutOfRangeError(intl).message;

      setFrom(startDate);
      setErrors(currentErrors => {
        if (rangeError) {
          return { ...currentErrors, from: rangeError };
        }

        if (currentErrors.from?.message !== outOfRangeMessage) {
          return currentErrors;
        }

        const nextErrors = { ...currentErrors };
        delete nextErrors.from;
        return nextErrors;
      });
    },
    [intl, setErrors, setFrom, startDateRange]
  );

  return {
    from,
    getStartDate,
    ...startDateRange,
    onChangeFrom,
    setFrom,
    validateStartDate
  };
};
